import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import seasonFormFields, { seasonSaveUrl } from '@form/seasonFormFields'
import { updateMissionSeasons, updateSeasonFormObject, initSeasonFormObject, updateErrors } from '@store/slice/missionSeasonSlice'
import { post, get } from '@util/xhrAPI'
import { getStringDate } from '@util/editDate'
import useDynamicForm from '@hook/useDynamicForm'
import { updateNotification } from '@store/slice/mainSlice'
import useDeleteButton from '@hook/useDeleteButton'
import useToggle from '@hook/useToggle'
import useStoreUpdate from '@hook/useStoreUpdate'
import useMainData from '@hook/useMainData'
import useFormData from '@hook/useFormData'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@table-library/react-table-library/theme'
import { getTheme } from '@table-library/react-table-library/baseline'
import {
  Table,
  Header,
  HeaderRow,
  Body,
  Row,
  HeaderCell,
  Cell,
} from "@table-library/react-table-library/table"
import Alert from '@ui/elements/Alert'
import Div from '@ui/layout/Div'
import Buttons from '@ui/elements/Buttons'
import Modal from '@ui/component/Modal'
import Form from '@ui/elements/Form'

const ListMissionSeasons = () => {
  const { missionSeasons, seasonFormObject, errors } = useSelector(state => state.missionSeason || { missionSeasons: [] })
  const { mission, data, local } = useSelector(state => state.main)
  const theme = useTheme(getTheme())
  const dispatch = useDispatch()
  const [t] = useTranslation(['common', 'season'])
  const loadData = useMainData()
  const deleteButton = useDeleteButton()
  const toggleDynamicForm = useDynamicForm()
  const renderFormData = useFormData()
  const [isLoading, toggleLoading] = useToggle(false)
  const [isModalActive, toggleModal] = useToggle(false)
  const [updateFormObject, updateFormObjectProp] = useStoreUpdate(dispatch, updateSeasonFormObject, { ...seasonFormObject, mission: mission.id })
  const dataMissionSeasons = { nodes: [] }

  if (missionSeasons) {
    dataMissionSeasons.nodes = Object.values(missionSeasons)
  }

  const removeMissionSeason = item => {
    const { id, links } = item
    get(`${import.meta.env.VITE_API_URL}${links.remove}`, response => {
      const { code } = response
      if (code === 200) {
        const missionSeason = missionSeasons[id]
        const missionSeasonsList = { ...missionSeasons }
        delete missionSeasonsList[id]
        dispatch(updateMissionSeasons({ ...missionSeasonsList }))
        dispatch(updateNotification({ type: 'is-success', message: t('messages.removed_successfuly', { title: missionSeason.name }) }))
      } else {
        dispatch(updateNotification({ type: 'is-danger', message: t('messages.error_occured') }))
      }
    })
  }

  const toggleSeasonUpdate = season => {
    dispatch(updateSeasonFormObject({ ...season, mission: mission.id }))
    toggleModal()
  }

  const generateFormStep = step => {
    return seasonFormFields(step, seasonFormObject, updateFormObject, updateFormObjectProp, saveForm, data, toggleDynamicForm, errors, isLoading, t)
  }

  const saveForm = e => {
    toggleLoading()
    e.preventDefault()
    post(seasonSaveUrl, renderFormData(seasonFormObject), response => {
      const { code, errors, missionSeason } = response
      if (code === 200) {
        dispatch(updateMissionSeasons({ ...missionSeasons, [missionSeason.id]: { ...missionSeason } }))
        dispatch(updateNotification({ type: 'is-success', message: t('notifications.mission_season_saved') }))
        dispatch(initSeasonFormObject())
      }
      if (Object.keys(errors).length) {
        dispatch(updateErrors(errors))
      }
      toggleLoading()
      toggleModal()
    })
  }

  const fetchInitialData = () => {
    const dataObject = {
      pilots: 'users[roles]=role_pilot'
    }
    loadData(dataObject)
  }

  useEffect(() => {
    get(`${import.meta.env.VITE_API_URL}/api/missions/${mission.id}/mission-seasons`, response => {
      const { missionSeasons } = response;
      dispatch(updateMissionSeasons(missionSeasons))
    });
  }, [])

  useEffect(() => {
    if (isModalActive && !data.pilots.length) {
      fetchInitialData()
    }
  }, [isModalActive])

  return (
    <Div cssClass='mission-seasons-list'>
      <h2 className='scene-title'>{t('season:titles.list_seasons')}</h2>
      <Div cssClass='actions-zone'>
        <Buttons buttons={{
          icon: 'icon-add',
          cssClass: 'is-info is-small',
          callback: toggleModal,
          text: t('season:actions.start_new_season')
        }}
        />
      </Div>
      {null !== missionSeasons && dataMissionSeasons.nodes.length
        ? <>
          <Table data={dataMissionSeasons} theme={theme}>
            {(tableList) => (
              <>
                <Header>
                  <HeaderRow>
                    <HeaderCell>{t('season:table.Season')}</HeaderCell>
                    <HeaderCell>{t('season:table.startsAt')}</HeaderCell>
                    <HeaderCell>{t('season:table.endsAt')}</HeaderCell>
                    <HeaderCell>{t('season:table.status')}</HeaderCell>
                    <HeaderCell></HeaderCell>
                  </HeaderRow>
                </Header>
                <Body>
                  {tableList.map(item =>
                    <Row key={item.id} item={item}>
                      <Cell>{item.name}</Cell>
                      <Cell>{getStringDate(item.startsAt, local)}</Cell>
                      <Cell>{getStringDate(item.endsAt, local)}</Cell>
                      <Cell>{item.isActive ? t('season:table.active') : t('season:table.closed')}</Cell>
                      <Cell className='table-actions'>
                        <Buttons buttons={[
                          {
                            icon: 'icon-pencil',
                            cssClass: 'is-info is-small',
                            callback: () => toggleSeasonUpdate(item)
                          }
                        ]} />
                        {deleteButton(item, item.name, removeMissionSeason)}
                      </Cell>
                    </Row>
                  )}
                </Body>
              </>
            )}
          </Table>
        </>
        : <Alert type='is-info' message={t('messages.empty_list')} />
      }

      <Modal cssClass='create-season-modal' isActive={isModalActive} closeCallback={toggleModal} closeAction={true}>
        <h3>{t(`season:titles.create_season`)}</h3>
        <Form>
          <Div cssClass="columns is-multiline">
            {generateFormStep('season')}
          </Div>

          <Div cssClass='column is-12 mission-form-bloc'>
            <h4 className='block-title'>{t('season:form.mission_first_scan')}</h4>
            <Div cssClass='columns is-multiline'>
              <Div cssClass='columns is-multiline dates'>
                {generateFormStep('firstScan')}
              </Div>
            </Div>
          </Div>
          <Div cssClass='column is-12 mission-form-bloc'>
            <h4 className='block-title'>{t('season:form.mission_second_scan')}</h4>
            <Div cssClass='columns is-multiline'>
              <Div cssClass='columns is-multiline dates'>
                {generateFormStep('secondScan')}
              </Div>
            </Div>
          </Div>
          <Div cssClass='column is-12 mission-form-bloc'>
            {generateFormStep('submit')}
          </Div>
        </Form>
      </Modal>
    </Div>
  )
}

export default ListMissionSeasons